// src/services/localNotifications.ts
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

function isWebNotificationSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

// Vrai si on peut afficher des notifications par au moins un moyen : natif
// (Capacitor Local Notifications) ou navigateur (Web Notification API).
// Aucun des deux ne dépend de Firebase / d'un service de push distant.
export function isNotificationsSupported(): boolean {
  return isNative() || isWebNotificationSupported();
}

// Convertit un identifiant arbitraire (uuid, "type-uuid", ...) en entier stable
// 31 bits, requis par @capacitor/local-notifications (id numérique).
export function stableNotificationId(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 2147483647;
}

export async function hasNotificationPermission(): Promise<boolean> {
  if (isNative()) {
    const { display } = await LocalNotifications.checkPermissions();
    return display === "granted";
  }
  if (isWebNotificationSupported()) return Notification.permission === "granted";
  return false;
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (isNative()) {
    const { display } = await LocalNotifications.requestPermissions();
    return display === "granted";
  }
  if (isWebNotificationSupported()) {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }
  return false;
}

export async function notifyNow(id: number, title: string, body: string, extra?: Record<string, unknown>) {
  if (!(await hasNotificationPermission())) return;

  if (isNative()) {
    await LocalNotifications.schedule({
      notifications: [
        {
          id,
          title,
          body,
          schedule: { at: new Date(Date.now() + 500) },
          extra,
        },
      ],
    });
    return;
  }

  if (isWebNotificationSupported()) {
    try {
      const notif = new Notification(title, { body, tag: String(id), data: extra });
      notif.onclick = () => {
        window.focus();
        notif.close();
      };
    } catch {
      // Certains navigateurs (ex: onglet non focus + restrictions plateforme) peuvent
      // refuser silencieusement l'instanciation ; ce n'est pas bloquant.
    }
  }
}

export async function scheduleReminder(
  id: number,
  title: string,
  body: string,
  at: Date,
  extra?: Record<string, unknown>
) {
  // Les rappels programmés (matchs/entraînements à venir) reposent sur la
  // planification native, qui survit à la fermeture de l'app. Sur navigateur,
  // il n'existe pas d'équivalent fiable sans service worker + push distant ;
  // on ne programme donc rien côté web (pas de notification silencieuse ratée).
  if (!isNative()) return;
  if (at.getTime() <= Date.now()) return;
  if (!(await hasNotificationPermission())) return;
  await LocalNotifications.schedule({
    notifications: [{ id, title, body, schedule: { at }, extra }],
  });
}

export async function cancelNotifications(ids: number[]) {
  if (!isNative() || ids.length === 0) return;
  await LocalNotifications.cancel({ notifications: ids.map((id) => ({ id })) });
}

export async function getPendingNotificationIds(): Promise<Set<number>> {
  if (!isNative()) return new Set();
  const { notifications } = await LocalNotifications.getPending();
  return new Set(notifications.map((n) => n.id));
}
